import React, { FunctionComponent, useRef, useState } from 'react'
import { Field, Form, FormSpy } from 'react-final-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { isEmpty, isEqual, noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { thisFieldRequiresResponse } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import { getLabelFromToken } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/tokens'
import { RadioButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { MessageToken } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Tokens'

import { InputFieldRFF } from '../../atoms/formElements/InputField'
import { RadioGroupRFF } from '../../atoms/formElements/RadioButtons'
import { SelectFieldRFF } from '../../atoms/formElements/SelectField'
import { TokenInputFieldRFF } from '../../atoms/formElements/TokenInputField'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TemplateDynamicText } from '../../atoms/templateDynamicText/TemplateDynamicText'
import { TertiaryButton } from '../../atoms/tertiaryButton/TertiaryButton'
import { Title } from '../../atoms/title/Title'
import { TokenTextInputRef } from '../../atoms/tokenTextInput/TokenTextInput.web'
import { TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface TemplateEditorProps {
  onCancel: () => void
  onSave?: (template: TemplateData) => void
  isLoading?: boolean
  initialValues?: TemplateData
  isSmartTemplatesEnabled?: boolean
}

export interface TemplateData {
  name?: string
  body?: string
  eventTrigger?: string
  timing?: string
  is_smart_send?: boolean
}

const Container = styled.View<{
  theme: ThemeType
}>(({ theme }: { theme: ThemeType }) => ({
  flex: 1,
  backgroundColor: theme.colors.backgroundPrimary,
}))

const Header = styled.View<{
  theme: ThemeType
}>(({ theme }: { theme: ThemeType }) => ({
  alignContent: 'center',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '14px',
  paddingBottom: theme.spacing['12px'],
  paddingLeft: theme.spacing['16px'],
  paddingRight: theme.spacing['16px'],
  borderBottomWidth: '1px',
  borderBottomColor: theme.colors.borderDefault,
}))

const FormContainer = styled.View<{
  theme: ThemeType
}>(({ theme }: { theme: ThemeType }) => ({
  backgroundColor: theme.colors.backgroundSecondary,
  flex: 1,
  gap: theme.spacing['16px'],
  padding: theme.spacing['16px'],
  overflow: 'auto',
}))

const ButtonsContainer = styled.View<{
  theme: ThemeType
}>(({ theme }: { theme: ThemeType }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  gap: theme.spacing['12px'],
}))

const DynamicTextContainer = styled.View<{
  theme: ThemeType
}>(({ theme }: { theme: ThemeType }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: theme.spacing['12px'],
}))

const TemplateOptionsContainer = styled.View<{
  theme: ThemeType
}>(({ theme }: { theme: ThemeType }) => ({
  flexDirection: 'row',
  justifyContent: 'flexStart',
  alignItems: 'flex-start',
  gap: theme.spacing['12px'],
  width: '100%',
}))

const SmartSendOptionsContainer = styled.View<{
  theme: ThemeType
}>(() => ({
  flexDirection: 'row',
  justifyContent: 'flexStart',
  alignItems: 'center',
  flex: 1,
}))

export const TemplateEditor: FunctionComponent<TemplateEditorProps> = ({
  onCancel,
  onSave,
  isLoading = false,
  initialValues,
  isSmartTemplatesEnabled,
}) => {
  const intl = useIntl()
  const { spacing } = useTheme()
  const [isSmartSend, setIsSmartSend] = useState(initialValues?.is_smart_send)
  const [formValues, setFormValues] = useState<TemplateData | null>(initialValues || null)

  const submitRef = useRef(noop)

  const handleSave = () => {
    // need this to trigger the errors for required fields
    submitRef.current?.()
    if (
      onSave &&
      formValues &&
      formValues?.body &&
      !isEmpty(formValues?.body.trim()) &&
      !isEmpty(formValues?.name?.trim())
    ) {
      onSave(formValues)
    }
  }

  const inputRef = useRef<TokenTextInputRef>(null)

  const handleDynamicTextPress = (token: MessageToken) => {
    inputRef?.current?.insertToken(token)
  }

  const templateTypeOptions = [
    ...(isSmartTemplatesEnabled
      ? [
          {
            label: intl.formatMessage({
              defaultMessage: 'Manual send',
              description: 'Option for manual send',
            }),
            value: 'manual_send',
          },
          {
            label: intl.formatMessage({
              defaultMessage: 'Smart send',
              description: 'Option for smart send',
            }),
            value: 'smart_send',
          },
        ]
      : []),
  ]

  return (
    <Container testID={tID('TemplateEditor')}>
      <Header testID={tID('TemplateEditor-header')}>
        <Title
          size={TitleSize.XSMALL}
          text={
            <FormattedMessage
              defaultMessage='{action} Template'
              description='Header for template editor'
              values={{ action: initialValues ? 'Edit' : 'Add' }}
            />
          }
          testID={tID('TemplateEditor-header')}
        />
        <ButtonsContainer>
          <TertiaryButton
            text={<FormattedMessage defaultMessage='Cancel' description='Button to cancel template creation' />}
            onPress={onCancel}
            testID={tID('TemplateEditor-cancel-button')}
          />
          <PrimaryButton
            text={<FormattedMessage defaultMessage='Save' description='Button to save template' />}
            onPress={handleSave}
            disabled={false}
            loading={isLoading}
            testID={tID('TemplateEditor-save-button')}
          />
        </ButtonsContainer>
      </Header>

      <FormContainer>
        <Form
          onSubmit={noop}
          initialValues={initialValues || {}}
          render={({ handleSubmit }) => {
            submitRef.current = handleSubmit
            return (
              <>
                <Field
                  name='name'
                  component={InputFieldRFF}
                  label={intl.formatMessage({
                    defaultMessage: 'Title',
                    description: 'Input for the name of the template',
                  })}
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Follow up',
                    description: 'Placeholder for the name of the template',
                  })}
                  validate={(value) => (value ? undefined : intl.formatMessage(thisFieldRequiresResponse))}
                  parse={(value) => {
                    setFormValues((prevValues) => ({
                      ...(prevValues || {}),
                      name: value,
                    }))
                    return value
                  }}
                  baseInputStyle={{ marginBottom: 24 }}
                  value={formValues?.name ?? ''}
                />
                <Field
                  name='body'
                  component={TokenInputFieldRFF}
                  label={intl.formatMessage({
                    defaultMessage: 'Body',
                    description: 'Input for the body of the template',
                  })}
                  testID={tID('TemplateEditor-body-field')}
                  inputRef={inputRef}
                  baseInputStyle={{ marginBottom: 0 }}
                  handleChange={(value: string) => {
                    setFormValues((prevValues) => ({
                      ...(prevValues || {}),
                      body: value,
                    }))
                  }}
                  fixedHeight={'200px'}
                  validate={(value) =>
                    value && value.trim() !== '' ? undefined : intl.formatMessage(thisFieldRequiresResponse)
                  }
                />

                <DynamicTextContainer>
                  {Object.values(MessageToken)?.map((token: string) => {
                    return (
                      <TemplateDynamicText
                        key={token as string}
                        name={intl.formatMessage(getLabelFromToken(token as MessageToken))}
                        onPress={() => handleDynamicTextPress(token as MessageToken)}
                      />
                    )
                  })}
                </DynamicTextContainer>
                {isSmartTemplatesEnabled && (
                  <TemplateOptionsContainer>
                    <Field
                      name='is_smart_send'
                      component={RadioGroupRFF}
                      label={intl.formatMessage({
                        defaultMessage: 'Template Type',
                        description: 'Input for the template type',
                      })}
                      buttons={templateTypeOptions}
                      buttonType={RadioButtonType.CIRCLE_TEXT_OUTLINE}
                      isRadioButtonsFlexRowDirection={true}
                      buttonStyle={{ marginRight: spacing['16px'] }}
                    />
                    {isSmartSend && (
                      <SmartSendOptionsContainer>
                        <Field
                          name='eventTrigger'
                          component={SelectFieldRFF}
                          label={intl.formatMessage({
                            defaultMessage: 'Event trigger',
                            description: 'Input for the event trigger',
                          })}
                          options={[
                            {
                              label: intl.formatMessage({
                                defaultMessage: 'After booking',
                                description: 'Option for after booking',
                              }),
                              value: 'after_booking',
                            },
                            {
                              label: intl.formatMessage({
                                defaultMessage: 'Before initial session',
                                description: 'Option for before first session and intake is completed',
                              }),
                              value: 'before_initial_session_intake_complete',
                            },
                            {
                              label: intl.formatMessage({
                                defaultMessage: 'Before initial session (intake not complete)',
                                description: 'Option for before first session and intake is NOT completed',
                              }),
                              value: 'before_initial_session_intake_not_complete',
                            },
                            {
                              label: intl.formatMessage({
                                defaultMessage: 'After intake session',
                                description: 'Option for after intake session',
                              }),
                              value: 'after_intake_session',
                            },
                            {
                              label: intl.formatMessage({
                                defaultMessage: 'Before each session',
                                description: 'Option for before each session',
                              }),
                              value: 'before_each_session',
                            },
                            {
                              label: intl.formatMessage({
                                defaultMessage: 'After each session',
                                description: 'Option for after each session',
                              }),
                              value: 'after_each_session',
                            },
                          ]}
                          baseInputStyle={{ width: 'calc(60% - 24px)', marginRight: spacing['24px'] }}
                        />
                        <Field
                          name='timing'
                          component={SelectFieldRFF}
                          label={intl.formatMessage({
                            defaultMessage: 'Timing',
                            description: 'Input for the timing',
                          })}
                          options={[
                            ...([
                              'before_each_session',
                              'before_initial_session_intake_not_complete',
                              'before_initial_session_intake_complete',
                            ].includes(formValues?.eventTrigger ?? '')
                              ? []
                              : [
                                  {
                                    label: intl.formatMessage({
                                      defaultMessage: 'Immediately',
                                      description: 'Option for immediatelly',
                                    }),
                                    value: 'immediately',
                                  },
                                ]),
                            {
                              label: intl.formatMessage({
                                defaultMessage: '30 min',
                                description: 'Option for 30 min',
                              }),
                              value: '30_min',
                            },
                            {
                              label: intl.formatMessage({
                                defaultMessage: '60 min',
                                description: 'Option for 60 min',
                              }),
                              value: '60_min',
                            },
                            {
                              label: intl.formatMessage({
                                defaultMessage: '24 hrs',
                                description: 'Option for 24 hrs',
                              }),
                              value: '24_hrs',
                            },
                            {
                              label: intl.formatMessage({
                                defaultMessage: '48 hrs',
                                description: 'Option for 48 hrs',
                              }),
                              value: '48_hrs',
                            },
                            {
                              label: intl.formatMessage({
                                defaultMessage: '72 hrs',
                                description: 'Option for 72 hrs',
                              }),
                              value: '72_hrs',
                            },
                          ]}
                          baseInputStyle={{ width: '40%' }}
                        />
                      </SmartSendOptionsContainer>
                    )}
                  </TemplateOptionsContainer>
                )}
                <FormSpy
                  subscription={{ values: true }}
                  onChange={(formState) => {
                    setIsSmartSend(formState?.values?.is_smart_send)
                    setFormValues((prevValues) => {
                      if (!isEqual(prevValues, formState.values)) {
                        return {
                          ...(prevValues || {}),
                          ...(formState.values?.is_smart_send ? { is_smart_send: formState.values.is_smart_send } : {}),
                          ...(formState.values?.eventTrigger ? { eventTrigger: formState.values.eventTrigger } : {}),
                          ...(formState.values?.timing ? { timing: formState.values.timing } : {}),
                        }
                      }
                      return prevValues
                    })
                  }}
                />
              </>
            )
          }}
          style={{ overflow: 'auto' }}
        />
      </FormContainer>
    </Container>
  )
}
